import type {
  InstalledCorpusPack,
  OperationResult,
  PackInstallPreview,
  PackSignatureStatus,
  PackSource,
  PackType,
  PackUninstallPreview,
} from '@/domain/Models';
import type { ValidatedCorpus } from './PackageModels';
import {
  db,
  type InstalledPackRow,
  type VocabularyEntryRow,
} from './Database';
import { putAudioBlob } from './AudioStore';
import { sha256Hex } from './Crypto';

interface StagedInstall {
  pack: ValidatedCorpus;
  bundled: boolean;
  sizeBytes: number;
}

const CONTENT_TABLES = [
  db.vocabulary,
  db.phrases,
  db.examples,
  db.scenarios,
  db.dialogueTurns,
  db.dialoguePairs,
  db.pairWords,
  db.pairPhrases,
  db.audioAssets,
  db.audioFileIndex,
  db.audioBlobs,
  db.installedPacks,
  db.packDependencies,
  db.packContentMembership,
  db.contentIdRegistry,
  db.packAudioMembership,
  db.meta,
] as const;

export async function enabledContentIds(itemType: string): Promise<Set<string>> {
  const enabled = await db.installedPacks.where('enabled').equals(1).primaryKeys();
  if (enabled.length === 0) {
    // Pre-registry content: treat every active row as visible.
    return new Set();
  }
  const rows = await db.packContentMembership
    .where('packageId')
    .anyOf(enabled as string[])
    .and((r) => r.itemType === itemType)
    .toArray();
  return new Set(rows.map((r) => r.contentId));
}

export function rowToInstalled(row: InstalledPackRow): InstalledCorpusPack {
  return {
    packageId: row.packageId,
    packageKey: row.packageKey,
    namespace: row.namespace,
    packType: row.packType as PackType,
    title: row.title,
    description: row.description,
    contentVersion: row.contentVersion,
    revision: row.revision,
    schemaVersion: row.schemaVersion,
    required: row.required === 1,
    enabled: row.enabled === 1,
    source: row.source as PackSource,
    signatureKeyId: row.signatureKeyId,
    signatureStatus: row.signatureStatus as PackSignatureStatus,
    manifestSha256: row.manifestSha256,
    installedSizeBytes: row.installedSizeBytes,
    vocabularyCount: row.vocabularyCount,
    phraseCount: row.phraseCount,
    exampleCount: row.exampleCount,
    scenarioCount: row.scenarioCount,
    articleCount: row.articleCount,
    installedAtEpochMs: row.installedAtEpochMs,
    updatedAtEpochMs: row.updatedAtEpochMs,
  };
}

export class PackInstaller {
  private installs = new Map<string, StagedInstall[]>();
  private uninstalls = new Map<string, PackUninstallPreview>();

  async listInstalled(): Promise<InstalledCorpusPack[]> {
    const rows = await db.installedPacks.toArray();
    return rows.map(rowToInstalled);
  }

  async inspect(
    packs: ValidatedCorpus[],
    options: { bundled?: boolean; sizeBytes?: number } = {},
  ): Promise<PackInstallPreview> {
    const planId = `plan-${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
    const staged: StagedInstall[] = packs.map((pack) => ({
      pack,
      bundled: options.bundled === true,
      sizeBytes: options.sizeBytes ?? estimateSize(pack),
    }));
    this.installs.set(planId, staged);

    const packsToInstall: InstalledCorpusPack[] = [];
    const alreadyInstalled: string[] = [];
    const contentConflicts: PackInstallPreview['contentConflicts'] = [];
    let contentAdded = 0;
    let contentUpdated = 0;
    let contentReused = 0;
    const errors: string[] = [];

    for (const item of staged) {
      const existing = await db.installedPacks.get(item.pack.manifest.packageId);
      if (existing && existing.contentVersion === item.pack.manifest.contentVersion) {
        alreadyInstalled.push(item.pack.manifest.packageId);
        continue;
      }
      for (const word of item.pack.vocabulary) {
        const prior = await db.vocabulary.get(word.id);
        if (!prior) {
          contentAdded += 1;
          continue;
        }
        if (prior.contentHash === word.contentHash) contentReused += 1;
        else {
          const owners = await db.packContentMembership
            .where('[contentId+itemType]')
            .equals([word.id, 'WORD'])
            .toArray();
          if (owners.some((o) => o.packageId !== item.pack.manifest.packageId)) {
            contentConflicts.push({
              contentId: word.id,
              itemType: 'WORD',
              reason: 'CONTENT_HASH_CONFLICT',
            });
          } else {
            contentUpdated += 1;
          }
        }
      }
      packsToInstall.push(await this.toInstalledView(item));
    }

    if (contentConflicts.length > 0) {
      errors.push('CONTENT_HASH_CONFLICT');
    }

    return {
      planId,
      packsToInstall,
      packsToUpgrade: [],
      alreadyInstalled,
      dependenciesSatisfied: true,
      contentAdded,
      contentUpdated,
      contentReused,
      contentConflicts,
      audioBytesAdded: staged.reduce((n, s) => n + s.sizeBytes, 0),
      audioBytesReused: 0,
      sessionsPreserved: await db.vocabCheckpoints.where('status').equals('IN_PROGRESS').count(),
      sessionsAffected: 0,
      errors,
      warnings: [],
    };
  }

  async commit(planId: string): Promise<OperationResult> {
    const staged = this.installs.get(planId);
    if (!staged) {
      return { ok: false, errorCode: 'PREVIEW_NOT_FOUND', message: '预览已失效，请重新选择文件' };
    }
    try {
      const hashes = new Map<string, string>();
      for (const item of staged) {
        const bytes = item.pack.files.get('manifest.json') ?? new Uint8Array();
        hashes.set(item.pack.manifest.packageId, await sha256Hex(bytes));
      }
      await db.transaction('rw', [...CONTENT_TABLES], async () => {
        for (const item of staged) {
          await this.installOne(item, hashes.get(item.pack.manifest.packageId) ?? '');
        }
      });
      this.installs.delete(planId);
      return { ok: true, errorCode: null, message: '语料包已安装' };
    } catch (e) {
      this.installs.delete(planId);
      return {
        ok: false,
        errorCode: String(e).replace(/^Error:\s*/, '').split(':')[0] ?? 'PACK_INSTALL_FAILED',
        message: '语料安装失败，已回滚',
      };
    }
  }

  discard(planId: string): void {
    this.installs.delete(planId);
  }

  async setEnabled(packageId: string, enabled: boolean): Promise<OperationResult> {
    const row = await db.installedPacks.get(packageId);
    if (!row) return { ok: false, errorCode: 'PACK_NOT_FOUND', message: '未找到语料包' };
    if (row.required === 1 && !enabled) {
      return { ok: false, errorCode: 'REQUIRED_PACK', message: '基础包不能停用' };
    }
    await db.installedPacks.update(packageId, { enabled: enabled ? 1 : 0, updatedAtEpochMs: Date.now() });
    return { ok: true, errorCode: null, message: enabled ? '已启用' : '已停用' };
  }

  async inspectUninstall(packageId: string): Promise<PackUninstallPreview> {
    const row = await db.installedPacks.get(packageId);
    const dependents = await db.packDependencies.where('dependencyPackageId').equals(packageId).toArray();
    const membership = row
      ? await db.packContentMembership.where('packageId').equals(packageId).toArray()
      : [];
    let reusedContentKept = 0;
    let itemsBecomingUnavailable = 0;
    for (const m of membership) {
      const others = await db.packContentMembership
        .where('[contentId+itemType]')
        .equals([m.contentId, m.itemType])
        .and((r) => r.packageId !== packageId)
        .count();
      if (others > 0) reusedContentKept += 1;
      else itemsBecomingUnavailable += 1;
    }
    const preview: PackUninstallPreview = {
      previewId: `un-${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`,
      packageId,
      title: row?.title ?? packageId,
      estimatedFreeableBytes: row?.installedSizeBytes ?? 0,
      reusedContentKept,
      itemsBecomingUnavailable,
      blockedByDependents: dependents.map((d) => d.packageId),
      requiredPack: row?.required === 1,
    };
    this.uninstalls.set(preview.previewId, preview);
    return preview;
  }

  async confirmUninstall(previewId: string): Promise<OperationResult> {
    const preview = this.uninstalls.get(previewId);
    if (!preview) {
      return { ok: false, errorCode: 'PREVIEW_NOT_FOUND', message: '卸载预览已失效' };
    }
    if (preview.requiredPack) {
      this.uninstalls.delete(previewId);
      throw new Error('REQUIRED_PACK');
    }
    if (preview.blockedByDependents.length > 0) {
      return { ok: false, errorCode: 'PACK_HAS_DEPENDENTS', message: '有其他语料包依赖此包' };
    }
    const packageId = preview.packageId;
    await db.transaction('rw', CONTENT_TABLES, async () => {
      const membership = await db.packContentMembership.where('packageId').equals(packageId).toArray();
      for (const m of membership) {
        const others = await db.packContentMembership
          .where('[contentId+itemType]')
          .equals([m.contentId, m.itemType])
          .and((r) => r.packageId !== packageId)
          .count();
        if (others === 0) {
          await deleteContent(m.itemType, m.contentId);
        }
      }
      await db.packContentMembership.where('packageId').equals(packageId).delete();
      await db.packAudioMembership.where('packageId').equals(packageId).delete();
      await db.packDependencies.where('packageId').equals(packageId).delete();
      await db.installedPacks.delete(packageId);
    });
    this.uninstalls.delete(previewId);
    return { ok: true, errorCode: null, message: '已卸载语料包' };
  }

  private async installOne(item: StagedInstall, manifestSha: string): Promise<void> {
    const pack = item.pack;
    const now = Date.now();
    const packageId = pack.manifest.packageId;
    const packageKey = `${packageId}:${pack.manifest.contentVersion}`;

    await upsertVocabulary(pack);
    await db.phrases.bulkPut(
      pack.phrases.map((p) => ({
        id: p.id,
        industry: p.industry ?? '',
        scene: p.scene ?? '',
        category: p.category ?? '',
        textEn: p.textEn,
        textZh: p.textZh,
        linkedTermIdsJson: JSON.stringify(p.linkedTermIds ?? []),
        sourceType: p.sourceType ?? '',
        audioAssetId: p.audioAssetId,
        contentHash: p.contentHash,
        active: 1,
      })),
    );
    await db.examples.bulkPut(
      pack.examples.map((p) => ({
        id: p.id,
        industry: p.industry ?? '',
        scene: p.scene ?? '',
        speaker: p.speaker ?? '',
        textEn: p.textEn,
        textZh: p.textZh ?? '',
        linkedTermIdsJson: JSON.stringify(p.linkedTermIds ?? []),
        dialogueGroupId: p.dialogueGroupId ?? null,
        sourceType: p.sourceType ?? '',
        audioAssetId: p.audioAssetId,
        contentHash: p.contentHash,
        active: 1,
      })),
    );
    await db.scenarios.bulkPut(
      pack.scenarios.map((s) => ({
        id: s.id,
        title: s.title,
        topic: s.topic,
        salesStage: s.salesStage,
        customerRole: s.customerRole,
        difficulty: s.difficulty,
        projectType: s.projectType,
        estimatedMinutes: s.estimatedMinutes,
        description: s.description ?? null,
        contentHash: s.contentHash,
        active: 1,
      })),
    );
    await db.dialogueTurns.bulkPut(
      pack.turns.map((t) => ({
        id: t.id,
        scenarioId: t.scenarioId,
        turnNo: t.turnNo,
        speaker: t.speaker,
        textEn: t.textEn,
        textZh: t.textZh ?? null,
        hint: t.hint ?? null,
        audioAssetId: t.audioAssetId ?? null,
        contentHash: t.contentHash,
      })),
    );
    await db.dialoguePairs.bulkPut(
      pack.pairs.map((p) => ({
        id: p.id,
        scenarioId: p.scenarioId,
        pairIndex: p.pairIndex,
        customerTurnId: p.customerTurnId,
        salesTurnId: p.salesTurnId,
        referenceCoreEn: p.referenceCoreEn,
        referenceChineseHint: p.referenceChineseHint,
        formalAlternativesJson: JSON.stringify(p.formalAlternatives ?? []),
        scoringPointsJson: JSON.stringify(p.scoringPoints ?? []),
        riskNote: p.riskNote ?? null,
        contentHash: p.contentHash,
      })),
    );
    await db.pairWords.bulkPut(
      pack.pairWords.map((p) => ({
        pairId: p.pairId,
        wordId: p.wordId,
        sortOrder: p.sortOrder ?? 0,
      })),
    );
    await db.pairPhrases.bulkPut(
      pack.pairPhrases.map((p) => ({
        pairId: p.pairId,
        phraseId: p.phraseId,
        sortOrder: p.sortOrder ?? 0,
      })),
    );
    await db.audioAssets.bulkPut(
      pack.audioAssets.map((a) => ({
        id: a.id,
        kind: a.kind,
        relativePath: a.relativePath,
        sha256: a.sha256,
        mimeType: a.mimeType ?? 'audio/mp4',
        codec: a.codec ?? 'aac-lc',
        durationMs: a.durationMs,
        sizeBytes: a.sizeBytes,
      })),
    );
    for (const asset of pack.audioAssets) {
      const data = pack.files.get(asset.relativePath);
      if (data) {
        const localKey = `corpus/${asset.id}`;
        await putAudioBlob(localKey, data, asset.mimeType ?? 'audio/mp4');
        await db.audioFileIndex.put({ assetId: asset.id, localKey, source: 'CORPUS' });
        await db.packAudioMembership.put({
          packageId,
          sha256: asset.sha256,
          relativePath: asset.relativePath,
        });
      }
    }

    const memberships = [
      ...pack.vocabulary.map((v) => ({
        packageId,
        contentId: v.id,
        itemType: 'WORD' as const,
        contentHash: v.contentHash,
        learningHash: v.contentHash,
      })),
      ...pack.phrases.map((v) => ({
        packageId,
        contentId: v.id,
        itemType: 'PHRASE' as const,
        contentHash: v.contentHash,
        learningHash: v.contentHash,
      })),
      ...pack.examples.map((v) => ({
        packageId,
        contentId: v.id,
        itemType: 'EXAMPLE' as const,
        contentHash: v.contentHash,
        learningHash: v.contentHash,
      })),
      ...pack.scenarios.map((v) => ({
        packageId,
        contentId: v.id,
        itemType: 'SCENARIO' as const,
        contentHash: v.contentHash,
        learningHash: v.contentHash,
      })),
    ];
    await db.packContentMembership.bulkPut(memberships);

    await db.installedPacks.put({
      packageId,
      packageKey,
      namespace: 'bess',
      packType: 'CONTENT',
      title: pack.manifest.packageId,
      description: '',
      contentVersion: pack.manifest.contentVersion,
      revision: 1,
      schemaVersion: pack.manifest.schemaVersion,
      required: item.bundled ? 1 : 0,
      enabled: 1,
      source: item.bundled ? 'BUNDLED' : 'IMPORTED',
      signatureKeyId: '',
      signatureStatus: pack.signatureStatus,
      manifestSha256: manifestSha,
      installedSizeBytes: item.sizeBytes,
      vocabularyCount: pack.vocabulary.length,
      phraseCount: pack.phrases.length,
      exampleCount: pack.examples.length,
      scenarioCount: pack.scenarios.length,
      articleCount: 0,
      installedAtEpochMs: now,
      updatedAtEpochMs: now,
    });
    await db.meta.put({ key: 'active_corpus_content_version', value: pack.manifest.contentVersion });
    await db.meta.put({ key: 'active_corpus_package_key', value: packageKey });
  }

  private async toInstalledView(item: StagedInstall): Promise<InstalledCorpusPack> {
    const pack = item.pack;
    return {
      packageId: pack.manifest.packageId,
      packageKey: `${pack.manifest.packageId}:${pack.manifest.contentVersion}`,
      namespace: 'bess',
      packType: 'CONTENT',
      title: pack.manifest.packageId,
      description: '',
      contentVersion: pack.manifest.contentVersion,
      revision: 1,
      schemaVersion: pack.manifest.schemaVersion,
      required: item.bundled,
      enabled: true,
      source: item.bundled ? 'BUNDLED' : 'IMPORTED',
      signatureKeyId: '',
      signatureStatus: pack.signatureStatus,
      manifestSha256: '',
      installedSizeBytes: item.sizeBytes,
      vocabularyCount: pack.vocabulary.length,
      phraseCount: pack.phrases.length,
      exampleCount: pack.examples.length,
      scenarioCount: pack.scenarios.length,
      articleCount: 0,
      installedAtEpochMs: 0,
      updatedAtEpochMs: 0,
    };
  }
}

async function upsertVocabulary(pack: ValidatedCorpus): Promise<void> {
  const rows: VocabularyEntryRow[] = pack.vocabulary.map((item) => ({
    id: item.id,
    term: item.term,
    normalizedTerm: item.normalizedTerm,
    ipa: item.ipa,
    partOfSpeech: item.partOfSpeech ?? '',
    chineseGloss: item.chineseGloss,
    englishDefinition: item.englishDefinition ?? null,
    collocationsJson: JSON.stringify(item.collocations ?? []),
    exampleSentenceEn: item.exampleSentenceEn,
    exampleSentenceZh: item.exampleSentenceZh ?? null,
    commonMistakes: item.commonMistakes,
    topic: item.topic,
    scenarioTagsJson: JSON.stringify(item.scenarioTags ?? []),
    cefrLevel: item.cefrLevel,
    wordAudioAssetId: item.wordAudioAssetId,
    exampleAudioAssetId: item.exampleAudioAssetId,
    contentSource: item.contentSource ?? 'CORE',
    contentHash: item.contentHash,
    active: 1,
  }));
  await db.vocabulary.bulkPut(rows);
}

async function deleteContent(itemType: string, contentId: string): Promise<void> {
  if (itemType === 'WORD') await db.vocabulary.delete(contentId);
  else if (itemType === 'PHRASE') await db.phrases.delete(contentId);
  else if (itemType === 'EXAMPLE') await db.examples.delete(contentId);
  else if (itemType === 'SCENARIO') {
    const pairs = await db.dialoguePairs.where('scenarioId').equals(contentId).toArray();
    for (const pair of pairs) {
      await db.pairWords.where('pairId').equals(pair.id).delete();
      await db.pairPhrases.where('pairId').equals(pair.id).delete();
      await db.dialoguePairs.delete(pair.id);
    }
    await db.dialogueTurns.where('scenarioId').equals(contentId).delete();
    await db.scenarios.delete(contentId);
  }
}

function estimateSize(pack: ValidatedCorpus): number {
  let n = 0;
  for (const bytes of pack.files.values()) n += bytes.byteLength;
  return n;
}

export const packInstaller = new PackInstaller();
